/**
 * Fundamental rubric-score provider for the injected Score column.
 *
 * Scores come from the global research library (`GET /v1/runs/recent?scope=
 * global`), wheel recommendations, and native fetch responses captured when
 * Rubric Band is clicked. Rows whose rubric band is not "No report" should
 * always get a numeric score — we never treat a failed lookup as "no report"
 * for those tickers.
 */

const CACHE_TTL_MS = 6 * 60 * 60 * 1000;
const RUNS_BULK_TTL_MS = 10 * 60 * 1000;
const STORAGE_PREFIX = "sfe:rubric-score:v3:";
const MAX_CONCURRENT = 12;
const RUNS_PAGE_SIZE = 500;

/** number = score, null = confirmed no report (only for "No report" rows). */
export type ScoreValue = number | null;

interface CacheEntry {
  score: ScoreValue;
  ts: number;
  confirmed: boolean;
}

const memoryCache = new Map<string, CacheEntry>();
const inflight = new Map<string, Promise<number | undefined>>();
const queue: string[] = [];
let activeRequests = 0;
let bulkFetchedAt = 0;
let bulkPromise: Promise<void> | null = null;
const repaintListeners = new Set<() => void>();
let hooksInstalled = false;

const SCORE_KEY_RE =
  /^(normalized_score|score|rubric_score|fundamental_score|composite_score|overall_score)$/i;
const TICKER_FIELDS = ["ticker", "symbol", "underlying", "underlying_ticker"] as const;

function normalizeTicker(ticker: string): string {
  const trimmed = ticker.trim().toUpperCase();
  const m = trimmed.match(/^([A-Z]{1,6})\b/);
  return m ? m[1] : trimmed.replace(/[^A-Z0-9.-]/g, "").slice(0, 6);
}

function readStorage(ticker: string): CacheEntry | null {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + ticker);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry;
    if (typeof parsed?.ts !== "number") return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeStorage(ticker: string, entry: CacheEntry): void {
  try {
    localStorage.setItem(STORAGE_PREFIX + ticker, JSON.stringify(entry));
  } catch {
    /* quota / disabled */
  }
}

function isFresh(entry: CacheEntry): boolean {
  return Date.now() - entry.ts < CACHE_TTL_MS;
}

/** Cache a numeric score, or `null` only when explicitly confirmed (No report). */
function cacheScore(ticker: string, score: ScoreValue, confirmedNull = false): void {
  const key = normalizeTicker(ticker);
  if (!key) return;
  if (score == null && !confirmedNull) return;

  const entry: CacheEntry = {
    score,
    ts: Date.now(),
    confirmed: score != null || confirmedNull,
  };
  memoryCache.set(key, entry);
  writeStorage(key, entry);
  notifyRepaint();
}

export function getCachedScore(ticker: string): ScoreValue | undefined {
  const key = normalizeTicker(ticker);
  const mem = memoryCache.get(key);
  if (mem && isFresh(mem) && mem.confirmed) return mem.score;

  const stored = readStorage(key);
  if (stored && isFresh(stored) && stored.confirmed) {
    memoryCache.set(key, stored);
    return stored.score;
  }

  return undefined;
}

export function onScoresUpdated(listener: () => void): () => void {
  repaintListeners.add(listener);
  return () => repaintListeners.delete(listener);
}

function notifyRepaint(): void {
  for (const fn of repaintListeners) fn();
}

async function apiGet<T>(path: string): Promise<T | "not-found" | null> {
  try {
    const res = await fetch(path, {
      credentials: "include",
      headers: { Accept: "application/json" },
    });
    if (res.status === 404) return "not-found";
    if (!res.ok) return null;
    return (await res.json()) as T;
  } catch {
    return null;
  }
}

function pickScore(obj: unknown): number | null {
  return deepPickScore(obj);
}

function deepPickScore(obj: unknown, depth = 0): number | null {
  if (obj == null || depth > 10) return null;
  if (typeof obj !== "object") return null;

  if (Array.isArray(obj)) {
    for (const item of obj) {
      const found = deepPickScore(item, depth + 1);
      if (found != null) return found;
    }
    return null;
  }

  const record = obj as Record<string, unknown>;
  for (const [k, v] of Object.entries(record)) {
    if (!SCORE_KEY_RE.test(k)) continue;
    if (typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 100) {
      return Math.round(v);
    }
    if (typeof v === "string") {
      const n = parseFloat(v);
      if (Number.isFinite(n) && n >= 0 && n <= 100) return Math.round(n);
    }
  }

  for (const v of Object.values(record)) {
    if (v && typeof v === "object") {
      const found = deepPickScore(v, depth + 1);
      if (found != null) return found;
    }
  }
  return null;
}

function pickTicker(obj: unknown): string | null {
  if (!obj || typeof obj !== "object") return null;
  const record = obj as Record<string, unknown>;
  for (const key of TICKER_FIELDS) {
    const v = record[key];
    if (typeof v === "string" && v.trim()) return normalizeTicker(v);
  }
  return null;
}

/** Ingest API payloads — never write confirmed-null from ambiguous responses. */
export function ingestScorePayload(data: unknown): void {
  if (!data || typeof data !== "object") return;
  const root = data as Record<string, unknown>;

  if (Array.isArray(root.runs)) {
    for (const run of root.runs) {
      const ticker = pickTicker(run);
      if (!ticker) continue;
      const score = pickScore(run);
      if (score != null) cacheScore(ticker, score);
    }
  }

  if (Array.isArray(root.recommendations)) {
    for (const rec of root.recommendations) {
      const ticker = pickTicker(rec);
      if (!ticker) continue;
      const score = pickScore(rec);
      if (score != null) cacheScore(ticker, score);
    }
  }

  if (typeof root.ticker === "string") {
    const ticker = normalizeTicker(root.ticker);
    const score = pickScore(root) ?? pickScore(root.report);
    if (score != null) cacheScore(ticker, score);
  }
}

async function fetchRecommendations(): Promise<void> {
  for (const side of ["puts", "calls"] as const) {
    const data = await apiGet<{ recommendations?: unknown[] }>(
      `/api/v1/wheel/recommendations/${side}`,
    );
    if (!data || data === "not-found") continue;
    ingestScorePayload(data);
  }
}

async function fetchGlobalRunsPage(offset: number): Promise<number> {
  const data = await apiGet<{ runs?: unknown[] }>(
    `/v1/runs/recent?scope=global&limit=${RUNS_PAGE_SIZE}&offset=${offset}&sort=ticker&dir=asc`,
  );
  if (!data || data === "not-found") return 0;
  ingestScorePayload(data);
  return Array.isArray(data.runs) ? data.runs.length : 0;
}

async function fetchGlobalRunsBulk(): Promise<void> {
  await fetchRecommendations();
  let offset = 0;
  for (let page = 0; page < 40; page++) {
    const count = await fetchGlobalRunsPage(offset);
    if (count < RUNS_PAGE_SIZE) break;
    offset += count;
  }
}

function ensureGlobalRuns(onDone: () => void): void {
  if (Date.now() - bulkFetchedAt < RUNS_BULK_TTL_MS && bulkPromise == null) {
    onDone();
    return;
  }
  if (bulkPromise) {
    void bulkPromise.finally(onDone);
    return;
  }

  bulkPromise = fetchGlobalRunsBulk()
    .catch(() => {})
    .finally(() => {
      bulkFetchedAt = Date.now();
      bulkPromise = null;
      onDone();
    });
}

async function fetchTickerScore(ticker: string): Promise<number | undefined> {
  const key = normalizeTicker(ticker);

  const runsData = await apiGet<{ runs?: unknown[] }>(
    `/v1/runs/recent?scope=global&ticker=${encodeURIComponent(key)}&limit=20`,
  );
  if (runsData && runsData !== "not-found" && Array.isArray(runsData.runs)) {
    for (const run of runsData.runs) {
      const runTicker = pickTicker(run);
      if (runTicker !== key) continue;
      const score = pickScore(run);
      if (score != null) return score;
    }
  }

  const reportData = await apiGet<Record<string, unknown>>(
    `/v1/reports/${encodeURIComponent(key)}/latest`,
  );
  if (reportData && reportData !== "not-found") {
    const score = pickScore(reportData);
    if (score != null) return score;
  }

  for (const side of ["puts", "calls"] as const) {
    const recData = await apiGet<{ recommendations?: unknown[] }>(
      `/api/v1/wheel/recommendations/${side}`,
    );
    if (!recData || recData === "not-found" || !Array.isArray(recData.recommendations)) continue;
    for (const rec of recData.recommendations) {
      if (pickTicker(rec) !== key) continue;
      const score = pickScore(rec);
      if (score != null) return score;
    }
  }

  return undefined;
}

function pumpQueue(onResolved: (ticker: string) => void): void {
  while (activeRequests < MAX_CONCURRENT && queue.length) {
    const ticker = queue.shift()!;
    const cached = getCachedScore(ticker);
    if (typeof cached === "number" || inflight.has(ticker)) continue;

    activeRequests += 1;
    const promise = fetchTickerScore(ticker)
      .then((score) => {
        if (score != null) cacheScore(ticker, score);
        return score;
      })
      .finally(() => {
        activeRequests -= 1;
        inflight.delete(ticker);
        pumpQueue(onResolved);
      });

    inflight.set(ticker, promise);
    void promise.then(() => onResolved(ticker));
  }
}

export function ensureScores(tickers: string[], onResolved: (ticker: string) => void): void {
  ensureGlobalRuns(() => {
    onResolved("");

    for (const raw of tickers) {
      const ticker = normalizeTicker(raw);
      if (!ticker) continue;
      if (typeof getCachedScore(ticker) === "number") continue;
      if (inflight.has(ticker) || queue.includes(ticker)) continue;
      queue.push(ticker);
    }
    pumpQueue(onResolved);
  });
}

function scanElementForPopupScore(root: Element): void {
  const text = root.textContent ?? "";
  if (!/\d{1,3}\s*\/\s*100/.test(text)) return;

  const scoreMatch = text.match(/(\d{1,3})\s*\/\s*100/);
  if (!scoreMatch) return;
  const score = Number(scoreMatch[1]);
  if (!Number.isFinite(score) || score < 0 || score > 100) return;

  const header =
    root.querySelector("h1,h2,h3,h4,strong,[class*='title']")?.textContent ?? text.slice(0, 120);
  const tickerMatch = header.match(/\b([A-Z]{1,6})\b/);
  if (!tickerMatch) return;

  cacheScore(tickerMatch[1], score);
}

function installDomObserver(): void {
  const observer = new MutationObserver((mutations) => {
    for (const m of mutations) {
      for (const node of m.addedNodes) {
        if (!(node instanceof HTMLElement)) continue;
        scanElementForPopupScore(node);
        node.querySelectorAll("*").forEach((el) => scanElementForPopupScore(el));
      }
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

export function initScoreProvider(repaint: () => void): void {
  if (hooksInstalled) return;
  hooksInstalled = true;

  installDomObserver();
  onScoresUpdated(repaint);
  ensureGlobalRuns(repaint);
}
